/**
 * Time Tracking Operations — timer + manual time logging.
 *
 * Two modes:
 * 1. Timer: startTimer → stopTimer (calculates duration automatically)
 * 2. Manual: logTime (add a duration entry after the fact)
 *
 * Time entries stored as JSON in `task.timeEntries`.
 * `task.timeSpentMs` is the running total for quick access.
 */

import { getDb, getNow } from "./runtime.js"
import { ok, fail, type OpResult } from "./op-types.js"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimeEntry {
  id: string
  startedAt: string    // UTC ms
  endedAt?: string     // UTC ms (undefined if timer is running)
  durationMs: number
  note?: string
  source: "timer" | "manual"
}

export interface TimeTrackingInfo {
  isRunning: boolean
  timerStartedAt?: string
  totalMs: number
  entries: TimeEntry[]
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

/**
 * Start a timer on a task. Multiple timers can run simultaneously.
 */
export async function startTimer(taskId: string): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")
  if (task.timerStartedAt) return fail("Timer already running")

  const now = getNow()
  await db.totodo.update(taskId, {
    timerStartedAt: now,
    updatedAt: now,
  })
  return ok()
}

/**
 * Stop a running timer. Creates a TimeEntry and adds duration to total.
 */
export async function stopTimer(taskId: string, note?: string): Promise<OpResult<{ durationMs: number }>> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")
  if (!task.timerStartedAt) return fail("No timer running")

  const now = getNow()
  const startMs = Number(task.timerStartedAt)
  const nowMs = Number(now)
  const durationMs = Math.max(0, nowMs - startMs)

  const entry: TimeEntry = {
    id: crypto.randomUUID(),
    startedAt: task.timerStartedAt,
    endedAt: now,
    durationMs,
    note,
    source: "timer",
  }

  const existingEntries = parseTimeEntries(task.timeEntries)
  existingEntries.push(entry)

  const totalMs = (task.timeSpentMs ?? 0) + durationMs

  await db.totodo.update(taskId, {
    timerStartedAt: undefined,
    timeSpentMs: totalMs,
    timeEntries: JSON.stringify(existingEntries),
    updatedAt: now,
  })

  return ok({ durationMs })
}

/**
 * Log time manually (after the fact). Adds a manual TimeEntry.
 */
export async function logTime(taskId: string, durationMs: number, note?: string): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")
  if (durationMs <= 0) return fail("Duration must be positive")

  const now = getNow()

  const entry: TimeEntry = {
    id: crypto.randomUUID(),
    startedAt: now,
    endedAt: now,
    durationMs,
    note,
    source: "manual",
  }

  const existingEntries = parseTimeEntries(task.timeEntries)
  existingEntries.push(entry)

  const totalMs = (task.timeSpentMs ?? 0) + durationMs

  await db.totodo.update(taskId, {
    timeSpentMs: totalMs,
    timeEntries: JSON.stringify(existingEntries),
    updatedAt: now,
  })

  return ok()
}

/**
 * Delete a time entry by ID.
 */
export async function deleteTimeEntry(taskId: string, entryId: string): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task) return fail("Task not found")

  const entries = parseTimeEntries(task.timeEntries)
  const entry = entries.find(e => e.id === entryId)
  if (!entry) return fail("Entry not found")

  const filtered = entries.filter(e => e.id !== entryId)
  const totalMs = Math.max(0, (task.timeSpentMs ?? 0) - entry.durationMs)

  await db.totodo.update(taskId, {
    timeSpentMs: totalMs,
    timeEntries: JSON.stringify(filtered),
    updatedAt: getNow(),
  })

  return ok()
}

/**
 * Get time tracking info for a task (for UI display).
 */
export function getTimeTrackingInfo(task: { timerStartedAt?: string; timeSpentMs?: number; timeEntries?: string }): TimeTrackingInfo {
  return {
    isRunning: !!task.timerStartedAt,
    timerStartedAt: task.timerStartedAt,
    totalMs: task.timeSpentMs ?? 0,
    entries: parseTimeEntries(task.timeEntries),
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseTimeEntries(json: string | undefined): TimeEntry[] {
  if (!json) return []
  try {
    return JSON.parse(json)
  } catch {
    return []
  }
}

/**
 * Format milliseconds to human-readable duration.
 * Examples: "45 min", "1h 30m", "2h", "< 1 min"
 */
export function formatDuration(ms: number): string {
  if (ms < 60_000) return "< 1 min"
  const totalMinutes = Math.round(ms / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours === 0) return `${minutes} min`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}
